import { Injectable, effect, inject } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { NotifyService } from "@ui/notify";
import { RecordingEndService } from "./recording-end.service";
import { RecordingStateService } from "./recording-state.service";

@Injectable({ providedIn: "root" })
export class RecordingLimitService {
    private readonly _state = inject(RecordingStateService);
    private readonly _end = inject(RecordingEndService);
    private readonly _notify = inject(NotifyService);
    private readonly _t = inject(TranslocoService);

    private _autoStopAttempted = false;
    private _warned = false;

    constructor() {
        effect(
            () => {
                if (!this._state.isActive()) {
                    this._autoStopAttempted = false;
                    this._warned = false;
                    return;
                }

                if (this._state.limitWarningActive() && !this._warned) {
                    this._warned = true;
                    this._notify.warn(
                        this._t.translate("patient-transcriptions.recording-limit-warning-message", {
                            minutes: this._state.remainingMinutes(),
                        })
                    );
                }

                if (this._state.limitReached() && !this._autoStopAttempted) {
                    this._autoStopAttempted = true;
                    void this._autoStop();
                }
            },
            { allowSignalWrites: true }
        );
    }

    private async _autoStop(): Promise<void> {
        this._notify.warn(this._t.translate("patient-transcriptions.recording-limit-reached"));

        const patientId = this._state.patientId();
        const result = await this._end.endAndTranscribe();

        if (!result.ended) {
            this._notify.error(this._t.translate("patient-transcriptions.recording-limit-reached-error"));
            return;
        }

        if (result.transcriptionId !== null) {
            this._state.processingHandoff.set({ transcriptionId: result.transcriptionId, patientId });
        }
    }
}
